import { useCallback, useState } from "react";
import { QR_COLUMN_KEYS, type QRColumnKey } from "../pages/Accounting/ChartOfAccounts/run-report/constants";

export type NegativeFormat = "minus" | "paren";
export type HeaderLayout = "name-first" | "title-first";
export type Alignment = "left" | "center" | "right";
export type EmptyCellDisplay = "dash" | "blank";
export type Borders = "default" | "none" | "horizontal" | "vertical" | "inner" | "outer" | "horizontal-outer" | "vertical-outer";
export type FilterCondition = "equals" | "not-equals" | "contains" | "is-empty" | "is-not-empty";

export interface ReportPrefs {
  // Filters
  filterBy: string;
  filterCondition: FilterCondition;
  filterValue: string;
  // Columns
  hiddenColumns: QRColumnKey[];
  columnOrder: QRColumnKey[];
  columnRightAlign: Partial<Record<QRColumnKey, boolean>>;
  // Number format
  divideBy1000: boolean;
  hideCurrency: boolean;
  negativeFormat: NegativeFormat;
  negativeInRed: boolean;
  decimals: 0 | 2;
  // Header
  showCompanyLogo: boolean;
  showCompanyName: boolean;
  showReportTitle: boolean;
  showReportPeriod: boolean;
  headerLayout: HeaderLayout;
  headerAlignment: Alignment;
  // Footer
  showDatePrepared: boolean;
  showTimePrepared: boolean;
  footerAlignment: Alignment;
  emptyCellDisplay: EmptyCellDisplay;
  // Row
  bandedRowColour: boolean;
  // Gridlines
  borders: Borders;
  // Data
  displayDebitCredit: boolean;
}

const DEFAULT: ReportPrefs = {
  filterBy: "distributionAccount",
  filterCondition: "equals",
  filterValue: "",
  hiddenColumns: [],
  columnOrder: [...QR_COLUMN_KEYS],
  columnRightAlign: { amount: true, balance: true },
  divideBy1000: false,
  hideCurrency: false,
  negativeFormat: "minus",
  negativeInRed: false,
  decimals: 2,
  showCompanyLogo: false,
  showCompanyName: true,
  showReportTitle: true,
  showReportPeriod: true,
  headerLayout: "name-first",
  headerAlignment: "center",
  showDatePrepared: true,
  showTimePrepared: true,
  footerAlignment: "right",
  emptyCellDisplay: "blank",
  bandedRowColour: false,
  borders: "default",
  displayDebitCredit: false,
};

const KEY = "novala.runReportPrefs";

export function useReportPreferences() {
  const [prefs, setPrefs] = useState<ReportPrefs>(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) return { ...DEFAULT, ...JSON.parse(raw) };
    } catch {}
    return DEFAULT;
  });

  const save = useCallback((p: ReportPrefs) => {
    setPrefs(p);
    try { window.localStorage.setItem(KEY, JSON.stringify(p)); } catch {}
  }, []);

  const reset = useCallback(() => save(DEFAULT), [save]);

  return { prefs, save, reset };
}

export function fmtMoneyWithPrefs(n: number, prefs: ReportPrefs): string {
  const value = prefs.divideBy1000 ? n / 1000 : n;
  const abs = Math.abs(value);
  const d = prefs.decimals ?? 2;
  const formatted = abs.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
  const sym = prefs.hideCurrency ? "" : "$";
  if (value < 0) return prefs.negativeFormat === "paren" ? `(${sym}${formatted})` : `-${sym}${formatted}`;
  return `${sym}${formatted}`;
}